import { Badge, DateText, UserInfo } from '@/components';
import { For, type JSX, Show } from 'solid-js';
import type { PollAnswerValue, PollFieldResult, PollResults } from './api';

const CHOICE_TYPES = new Set(['single_choice', 'multi_choice', 'dropdown']);
const NUMBER_TYPES = new Set(['number', 'scale']);

const displayValue = (value: PollAnswerValue): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return `${value.start} – ${value.end}`;
  return String(value);
};

const ResultCard = (props: { field: PollFieldResult }) => {
  const maxCount = () => Math.max(1, ...props.field.options.map((option) => option.count));
  return (
    <section class="rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark">
      <div class="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 class="font-semibold">{props.field.label}</h2>
          <p class="text-xs text-neutral-500">{props.field.responseCount} answers</p>
        </div>
        <Show when={props.field.average !== null}>
          <Badge tone="info">Average {props.field.average?.toFixed(2)}</Badge>
        </Show>
      </div>

      <Show when={CHOICE_TYPES.has(props.field.type)}>
        <div class="flex flex-col gap-3">
          <For each={props.field.options}>
            {(option) => (
              <div>
                <div class="mb-1 flex justify-between gap-3 text-sm">
                  <span>{option.label}</span>
                  <span class="tabular-nums text-neutral-500">{option.count}</span>
                </div>
                <div class="h-2 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                  <div
                    class="h-full rounded-full bg-brand-600 transition-[width] motion-reduce:transition-none"
                    style={`width:${(option.count / maxCount()) * 100}%`}
                  />
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={NUMBER_TYPES.has(props.field.type)}>
        <div class="flex flex-wrap gap-2">
          <For each={props.field.distribution}>
            {(item) => (
              <Badge>
                {item.value}: {item.count}
              </Badge>
            )}
          </For>
        </div>
      </Show>

      <Show
        when={
          !CHOICE_TYPES.has(props.field.type) &&
          !NUMBER_TYPES.has(props.field.type) &&
          props.field.type !== 'section'
        }
      >
        <ul class="flex max-h-72 flex-col gap-2 overflow-y-auto text-sm">
          <For each={props.field.values}>
            {(value) => (
              <li class="rounded-lg bg-neutral-50 px-3 py-2 dark:bg-neutral-900">
                {displayValue(value)}
              </li>
            )}
          </For>
        </ul>
      </Show>
    </section>
  );
};

export const ResultsDashboard = (props: { results: PollResults; actions?: JSX.Element }) => (
  <div class="grid gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]">
    <div class="flex flex-col gap-4">
      <div class="grid gap-3 sm:grid-cols-2">
        <div class="rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark">
          <p class="text-sm text-neutral-500">Responses</p>
          <p class="mt-1 text-3xl font-semibold tabular-nums">{props.results.responseCount}</p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark">
          <p class="text-sm text-neutral-500">Privacy</p>
          <p class="mt-1 font-semibold">
            {props.results.poll.isAnonymous ? 'Anonymous' : 'Identified'}
          </p>
        </div>
      </div>
      <For each={props.results.fields}>
        {(field) =>
          field.type === 'section' ? (
            <h2 class="border-b border-neutral-200 pb-2 pt-3 text-lg font-semibold dark:border-neutral-800">
              {field.label}
            </h2>
          ) : (
            <ResultCard field={field} />
          )
        }
      </For>
      <Show when={props.results.respondents}>
        {(respondents) => (
          <section class="rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark">
            <h2 class="mb-4 font-semibold">Respondents</h2>
            <div class="flex flex-col divide-y divide-neutral-200 dark:divide-neutral-800">
              <For each={respondents()}>
                {(respondent) => (
                  <div class="py-3 first:pt-0 last:pb-0">
                    <div class="flex items-center justify-between gap-3">
                      <UserInfo user={respondent.user} />
                      <span class="text-xs text-neutral-500">
                        <DateText value={respondent.submittedAt} />
                      </span>
                    </div>
                    <dl class="mt-2 grid gap-1 pl-10 text-xs text-neutral-500">
                      <For each={respondent.answers}>
                        {(answer) => (
                          <div class="grid grid-cols-[minmax(0,10rem)_1fr] gap-2">
                            <dt class="truncate">
                              {
                                props.results.fields.find(
                                  (field) => field.fieldId === answer.fieldId,
                                )?.label
                              }
                            </dt>
                            <dd>{displayValue(answer.value)}</dd>
                          </div>
                        )}
                      </For>
                    </dl>
                  </div>
                )}
              </For>
            </div>
          </section>
        )}
      </Show>
    </div>

    <aside class="h-fit rounded-xl border border-neutral-200 bg-surface p-5 dark:border-neutral-800 dark:bg-surface-dark lg:sticky lg:top-24">
      <dl class="grid gap-3 text-sm">
        <div>
          <dt class="text-neutral-500">Creator</dt>
          <dd>{props.results.poll.createdBy.displayName || props.results.poll.createdBy.email}</dd>
        </div>
        <div>
          <dt class="text-neutral-500">Status</dt>
          <dd class="capitalize">{props.results.poll.status}</dd>
        </div>
        <div>
          <dt class="text-neutral-500">Closes</dt>
          <dd>
            <DateText value={props.results.poll.closesAt} empty="No deadline" />
          </dd>
        </div>
        <div>
          <dt class="text-neutral-500">Visibility</dt>
          <dd class="capitalize">{props.results.poll.resultsVisibility.replace('_', ' ')}</dd>
        </div>
      </dl>
      <Show when={props.actions}>
        <div class="mt-5 flex flex-col gap-2">{props.actions}</div>
      </Show>
    </aside>
  </div>
);
